/**
 * Market experience module (`/api/market`).
 * Tab-level 404 = module off for this tenant (not an error).
 * Preserve tenant id casing (evasys_V2 ≠ evasys_v2).
 */

import { clearHomeDestinationCache } from '@/hooks/useHomeDestination';

const API_BASE_URL =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

export class MarketModuleOffError extends Error {
  constructor() {
    super('Market module off');
    this.name = 'MarketModuleOffError';
    this.status = 404;
  }
}

export class MarketModuleFaultError extends Error {
  constructor(message) {
    super(message || 'Market module fault');
    this.name = 'MarketModuleFaultError';
  }
}

export function payloadHasFault(data) {
  return typeof data?.error === 'string' && data.error.trim() !== '';
}

function headers(tenantId) {
  const h = { 'Content-Type': 'application/json' };
  if (tenantId) h['X-Tenant-ID'] = tenantId;
  // Cookie session is primary auth; X-User-ID helps the API resolve the requester for enrich email.
  try {
    const userId = typeof localStorage !== 'undefined' ? localStorage.getItem('user-id') : null;
    if (userId) h['X-User-ID'] = userId;
  } catch {
    /* ignore */
  }
  return h;
}

/** Empty `country=` / `lms=` must be sent — omitting them falls back to saved prefs. */
const ALLOW_EMPTY_PARAMS = new Set(['country', 'lms']);

export function buildMarketQuery(tenantId, params = {}) {
  const qs = new URLSearchParams();
  if (tenantId) qs.set('tenant_id', tenantId);
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === 'all') return;
    if (typeof v === 'number' && Number.isNaN(v)) return;
    if (v === '' && ALLOW_EMPTY_PARAMS.has(k)) {
      qs.set(k, '');
      return;
    }
    if (v === '') return;
    const s = String(v).trim();
    if (s === 'all') return;
    if (!s) {
      if (ALLOW_EMPTY_PARAMS.has(k)) qs.set(k, '');
      return;
    }
    qs.set(k, s);
  });
  return qs.toString();
}

function buildQuery(tenantId, params = {}) {
  return buildMarketQuery(tenantId, params);
}

async function parseJson(res) {
  const text = await res.text().catch(() => '');
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

async function marketFetch(path, tenantId, { method = 'GET', params, body } = {}) {
  const qs = buildQuery(tenantId, params);
  const url = `${API_BASE_URL}/api/market${path}${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: headers(tenantId),
    body: body != null ? JSON.stringify(body) : undefined,
  });
  if (res.status === 404) {
    throw new MarketModuleOffError();
  }
  const data = await parseJson(res);
  if (!res.ok) {
    const message = data?.error === 'pilot_cap_reached'
      ? (data.message || data.error)
      : (data.error || data.message || `Market API failed (${res.status})`);
    const err = new Error(message);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  if (payloadHasFault(data)) {
    const err = new MarketModuleFaultError(data.error);
    err.status = 200;
    err.body = data;
    throw err;
  }
  return data;
}

export function isNeedsSetup(payload) {
  return Boolean(payload?.needs_setup);
}

const MARKET_MODULE_CACHE_KEY = 'market-module-hint-v1';

function readMarketModuleCache(tenantId) {
  if (!tenantId || typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(`${MARKET_MODULE_CACHE_KEY}:${tenantId}`);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return null;
    return data;
  } catch {
    return null;
  }
}

function writeMarketModuleCache(tenantId, hint) {
  if (!tenantId || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(`${MARKET_MODULE_CACHE_KEY}:${tenantId}`, JSON.stringify(hint));
  } catch {
    /* ignore */
  }
}

/**
 * Sync hint for UI that should not wait on /lenses+/summary.
 * @returns {{ on: boolean, lens: string|null } | null}
 */
export function getCachedMarketModuleHint(tenantId) {
  const cached = readMarketModuleCache(tenantId);
  if (!cached || typeof cached.on !== 'boolean') return null;
  return { on: cached.on, lens: cached.lens || null };
}

/**
 * One-call Market availability (lenses only). Prefer this for account-page CTAs.
 * @returns {{ on: boolean, lens: string|null, status: 'off'|'needs_setup'|'on'|'fault' }}
 */
export async function resolveMarketModuleHint(tenantId) {
  if (!tenantId) return { on: false, lens: null, status: 'off' };
  const cached = getCachedMarketModuleHint(tenantId);
  try {
    const lenses = await marketFetch('/lenses', tenantId);
    if (isNeedsSetup(lenses)) {
      const hint = { on: true, lens: lenses?.default_lens || lenses?.lenses?.[0]?.lens_key || null, status: 'needs_setup' };
      writeMarketModuleCache(tenantId, hint);
      return hint;
    }
    const lens = lenses?.default_lens || lenses?.lenses?.[0]?.lens_key || null;
    if (!lens && !(lenses?.lenses || []).length) {
      const hint = { on: false, lens: null, status: 'off' };
      writeMarketModuleCache(tenantId, hint);
      return hint;
    }
    const hint = { on: true, lens, status: 'on' };
    writeMarketModuleCache(tenantId, hint);
    return hint;
  } catch (e) {
    if (e instanceof MarketModuleOffError || e?.status === 404) {
      const hint = { on: false, lens: null, status: 'off' };
      writeMarketModuleCache(tenantId, hint);
      return hint;
    }
    // Keep a positive cache on transient faults so CTAs do not flicker off.
    if (cached?.on) {
      return { on: true, lens: cached.lens || null, status: 'fault' };
    }
    return { on: false, lens: null, status: 'fault' };
  }
}

/**
 * Probe tab-level /summary.
 * @returns {'off' | 'needs_setup' | 'on' | 'fault'}
 */
export async function probeMarketModule(tenantId) {
  if (!tenantId) return 'off';
  try {
    const lenses = await marketFetch('/lenses', tenantId);
    if (isNeedsSetup(lenses) || !(lenses?.lenses || []).length) {
      writeMarketModuleCache(tenantId, {
        on: true,
        lens: lenses?.default_lens || lenses?.lenses?.[0]?.lens_key || null,
      });
      return 'needs_setup';
    }
    const lens = lenses.default_lens || lenses.lenses[0]?.lens_key;
    writeMarketModuleCache(tenantId, { on: true, lens });
    // [2026-10-13] No /summary call here. It re-asked a question /lenses had just answered:
    // the BE returns needs_setup from /summary only when loadLens finds no active profile for
    // the key, and /lenses lists exactly the active profiles. It cost a full summary compute
    // (14.4s for constructor_tech) before the page could leave its skeleton, and the page then
    // fetched the same summary again with the user's filters.
    return 'on';
  } catch (e) {
    if (e instanceof MarketModuleOffError || e?.status === 404) {
      writeMarketModuleCache(tenantId, { on: false, lens: null });
      return 'off';
    }
    try {
      const data = await marketFetch('/summary', tenantId);
      if (isNeedsSetup(data)) return 'needs_setup';
      writeMarketModuleCache(tenantId, { on: true, lens: null });
      return 'on';
    } catch (e2) {
      if (e2 instanceof MarketModuleOffError || e2?.status === 404) {
        writeMarketModuleCache(tenantId, { on: false, lens: null });
        return 'off';
      }
      return 'fault';
    }
  }
}

export function fetchMarketLenses(tenantId) {
  return marketFetch('/lenses', tenantId);
}

export function createMarketLens(tenantId, payload) {
  return marketFetch('/lenses', tenantId, { method: 'POST', body: payload });
}

/** `extra.brief: 'none'` returns the numbers without composing the commentary (fast); omit it for the full summary. */
export function fetchMarketSummary(tenantId, lens, country, extra = {}) {
  return marketFetch('/summary', tenantId, {
    params: { lens, country, lms: extra.lms, category: extra.category, ...(extra.brief ? { brief: extra.brief } : {}) },
  });
}

export function fetchMarketComposition(tenantId, lens, country) {
  return marketFetch('/composition', tenantId, { params: { lens, country } });
}

/* 🔴 THIS DESTRUCTURE IS A WHITELIST, AND ANYTHING MISSING FROM IT IS DROPPED IN SILENCE.
 *
 * [2026-10-07] `category` was added to the caller and to the API on the same day and still did
 * nothing, because the parameter never left the browser: the control rendered, the request went
 * out without it, and the server correctly answered the unfiltered question. A dropdown that
 * changes nothing is worse than a missing one — it reports success on the wrong answer. Any new
 * filter must be added in BOTH places here. */
export function fetchMarketOpportunities(tenantId, params = {}) {
  const { lens, queue, country, type, lms, category, limit = 200, offset = 0 } = params;
  return marketFetch('/opportunities', tenantId, {
    params: {
      lens,
      queue,
      country,
      type,
      lms,
      category,
      limit,
      offset,
    },
  });
}

export function fetchPilotStatus(tenantId) {
  return marketFetch('/pilot-status', tenantId);
}

export function updatePilotStatus(tenantId, patch) {
  return marketFetch('/pilot-status', tenantId, { method: 'PUT', body: patch });
}

export function fetchMyMarketPreferences(tenantId) {
  return marketFetch('/my-preferences', tenantId);
}

export function putMyMarketPreferences(tenantId, patch) {
  return marketFetch('/my-preferences', tenantId, { method: 'PUT', body: patch });
}

export function fetchLmsFamilies(tenantId, country = '') {
  return marketFetch('/lms-families', tenantId, { params: { country } });
}

/** Key sector trends — AI commentary from verified market research (first section of Dynamics). */
export function fetchMarketDynamicsCommentary(tenantId, lens, country) {
  return marketFetch('/dynamics/commentary', tenantId, { params: { lens, country } });
}

export function fetchMarketPlayers(tenantId, lens, extra = {}) {
  return marketFetch('/players', tenantId, { params: { lens, ...extra } });
}

export function fetchVendorMatches(tenantId, lens, vendors, countries) {
  const list = Array.isArray(vendors) ? vendors.join(',') : vendors;
  const countriesParam = Array.isArray(countries)
    ? countries.filter(Boolean).join(',')
    : countries;
  return marketFetch('/vendor-matches', tenantId, {
    params: {
      lens,
      vendors: list,
      ...(countriesParam ? { countries: countriesParam } : {}),
    },
  });
}

export function fetchMarketAccountPreview(tenantId, accountId, lens) {
  return marketFetch(`/accounts/${encodeURIComponent(accountId)}/preview`, tenantId, { params: { lens } });
}

/** `include`: extra category keys outside the lens (the closed-deal page sends the deal's own). */
export function fetchMarketAccountTechStack(tenantId, accountId, lens, include) {
  return marketFetch(`/accounts/${encodeURIComponent(accountId)}/tech-stack`, tenantId, { params: { lens, include } });
}

/** Always send this on the Accounts in motion card (API max is 100). */
export const ACCOUNTS_IN_MOTION_LIMIT = 100;

const MOTION_DIRECTIONS = new Set(['toward', 'under_pressure', 'buying', 'flat']);

/** Recognised `direction` values only. Unrecognised → omit (API treats as unfiltered). */
export function sanitizeMotionDirection(direction) {
  if (direction == null) return undefined;
  const d = String(direction).trim().toLowerCase();
  if (!d) return undefined;
  return MOTION_DIRECTIONS.has(d) ? d : undefined;
}

export function buildAccountsInMotionParams({
  lens,
  country,
  limit = ACCOUNTS_IN_MOTION_LIMIT,
  direction,
} = {}) {
  const params = {
    lens,
    country,
    limit: limit ?? ACCOUNTS_IN_MOTION_LIMIT,
  };
  const d = sanitizeMotionDirection(direction);
  if (d) params.direction = d;
  return params;
}

/** Badge counts and `total` are over the full set; `direction` does not change them. */
export function accountsInMotionView(payload) {
  const rows = Array.isArray(payload?.rows) ? payload.rows : [];
  const showing = payload?.showing != null ? Number(payload.showing) : rows.length;
  return {
    rows,
    total: payload?.total,
    matched: payload?.matched,
    showing: Number.isFinite(showing) ? showing : rows.length,
    direction: payload?.direction ?? null,
    moving_toward: payload?.moving_toward,
    under_pressure: payload?.under_pressure,
    with_buying_evidence: payload?.with_buying_evidence,
  };
}

export function accountsInMotionFilterCaption({ showing, matched } = {}) {
  if (showing == null || matched == null) return null;
  const rest = Number(matched) > Number(showing) ? ' — raise the limit to see the rest' : '';
  return `Showing ${showing} of ${matched}${rest}`;
}

export function fetchMarketAccountsInMotion(tenantId, lens, country, limitOrOpts = ACCOUNTS_IN_MOTION_LIMIT, direction) {
  const opts = typeof limitOrOpts === 'object' && limitOrOpts !== null
    ? limitOrOpts
    : { limit: limitOrOpts, direction };
  return marketFetch('/accounts-in-motion', tenantId, {
    params: buildAccountsInMotionParams({
      lens,
      country,
      limit: opts.limit ?? ACCOUNTS_IN_MOTION_LIMIT,
      direction: opts.direction,
    }),
  });
}

export function fetchMarketChanges(tenantId, lens, country) {
  return marketFetch('/changes', tenantId, { params: { lens, country } });
}

export function markMarketChangesSeen(tenantId, ids, lens) {
  return marketFetch('/changes/seen', tenantId, { method: 'POST', params: { lens }, body: { ids } });
}

export function fetchMarketPreferences(tenantId, lens) {
  return marketFetch('/preferences', tenantId, { params: { lens } });
}

export function previewMarketPreferences(tenantId, patch, lens) {
  return marketFetch('/preferences/preview', tenantId, { method: 'POST', params: { lens }, body: patch });
}

export async function patchMarketPreferences(tenantId, patch, lens) {
  const qs = buildQuery(tenantId, { lens });
  const url = `${API_BASE_URL}/api/market/preferences${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, {
    method: 'PATCH',
    credentials: 'include',
    headers: headers(tenantId),
    body: patch != null ? JSON.stringify(patch) : undefined,
  });
  if (res.status === 404) {
    throw new MarketModuleOffError();
  }
  const data = await parseJson(res);
  if (!res.ok) {
    const message = data?.error === 'pilot_cap_reached'
      ? (data.message || data.error)
      : (data.error || data.message || `Market API failed (${res.status})`);
    const err = new Error(message);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  if (payloadHasFault(data)) {
    const err = new MarketModuleFaultError(data.error);
    err.status = 200;
    err.body = data;
    throw err;
  }
  return { status: res.status, data };
}

/** Poll until the deferred rescore leaves `running`. A dropped poll is retried. */
// [2026-11-04] 20 minutes, matching the API's stale cut-off: a large market (testwe, 5,600 accounts) rescored for longer
// than the old 5-minute limit, and the client stopped watching a rescore that was still running.
export async function pollMarketRecomputeStatus(tenantId, { scope, intervalMs = 3000, timeoutMs = 20 * 60_000 } = {}) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    try {
      const payload = await marketFetch('/preferences/recompute-status', tenantId, {
        params: scope === 'team' ? { scope: 'team' } : {},
      });
      if (payload?.state && payload.state !== 'running') return payload;
    } catch {
      /* a dropped poll is not a failure */
    }
  }
  return { state: 'unknown', timedOut: true };
}

export function deleteMyMarketLens(tenantId, lens) {
  return marketFetch('/my-lens', tenantId, { method: 'DELETE', params: { lens } });
}

function invalidateHomeAccountsCache() {
  try {
    sessionStorage.removeItem('home-cache-v4');
    sessionStorage.removeItem('home-cache-v5');
    sessionStorage.removeItem('home-cache-v6');
  } catch {
    /* ignore */
  }
}

function invalidateAccountListCaches(tenantId) {
  invalidateHomeAccountsCache();
  try {
    if (tenantId) {
      sessionStorage.removeItem(`accounts-cache-v2-${tenantId}`);
      sessionStorage.removeItem(`market-cache-v5:${tenantId}`);
    }
    sessionStorage.removeItem('market-cache-v2');
  } catch {
    /* ignore */
  }
}

export function clearAccountPageCache(tenantId, recordId) {
  if (!tenantId || !recordId) return;
  try {
    sessionStorage.removeItem(`account-final-v3-${tenantId}-${recordId}`);
  } catch {
    /* ignore */
  }
}

export function isMarketModuleTenantId(tenantId) {
  const id = String(tenantId || '').trim().toLowerCase();
  return /_v2$/.test(id) || id === 'bibliotheca';
}

/** Remove is a Market-module action — hide it when the module is off, and always in pilot mode. */
export function canShowRemoveAccountAction(tenantId, marketModuleOn = null, pilotMode = false) {
  if (pilotMode) return false;
  if (!tenantId) return false;
  if (marketModuleOn === false) return false;
  if (marketModuleOn === true) return true;
  const cached = getCachedMarketModuleHint(tenantId);
  if (cached?.on === false) return false;
  if (cached?.on === true) return true;
  return isMarketModuleTenantId(tenantId);
}

export async function addMarketAccount(tenantId, payload, lens) {
  const data = await marketFetch('/accounts/add', tenantId, { method: 'POST', params: { lens }, body: payload });
  invalidateAccountListCaches(tenantId);
  clearHomeDestinationCache();
  return data;
}

/** Hide a Market-added account from this tenant's list. The row is kept and restorable via add. */
export async function removeMarketAccount(tenantId, payload, lens) {
  const data = await marketFetch('/accounts/remove', tenantId, { method: 'POST', params: { lens }, body: payload });
  invalidateAccountListCaches(tenantId);
  clearHomeDestinationCache();
  return data;
}

export function markAccountNotRelevant(tenantId, accountId, payload = {}, lens) {
  return marketFetch(`/accounts/${encodeURIComponent(accountId)}/not-relevant`, tenantId, {
    method: 'POST',
    params: { lens },
    body: payload,
  });
}

/** Set an account aside on the Ignore list (removes it from opportunity queues). */
export function ignoreMarketAccount(tenantId, accountId, payload = {}, lens) {
  return marketFetch(`/accounts/${encodeURIComponent(accountId)}/ignore`, tenantId, {
    method: 'POST',
    params: { lens },
    body: payload,
  });
}

export function requestAccountEnrichment(tenantId, accountId, payload = {}, lens) {
  const body = { ...(payload || {}) };
  if (!body.scope && Array.isArray(body.unknowns)) {
    body.scope = body.unknowns
      .map((u) => (typeof u === 'string' ? u : u?.key || u?.field || u?.scope))
      .filter(Boolean);
    delete body.unknowns;
  }
  if (!body.scope || (Array.isArray(body.scope) && body.scope.length === 0)) {
    body.scope = ['personas'];
  }
  return marketFetch(`/accounts/${encodeURIComponent(accountId)}/enrich`, tenantId, {
    method: 'POST',
    params: { lens },
    body,
  });
}

export function fetchMarketOnboarding(tenantId, lens) {
  return marketFetch('/onboarding', tenantId, { params: { lens } });
}

export function fetchMarketRenewalRisk(tenantId, lens, country) {
  return marketFetch('/renewal-risk', tenantId, { params: { lens, country } });
}

export function fetchMarketIgnore(tenantId, lens, country) {
  return marketFetch('/ignore', tenantId, { params: { lens, country } });
}

/** Whole Funding tab. Empty country= means all entitled countries (same as Overview). */
export function fetchMarketFunding(tenantId, lens, country) {
  const countryParam = country == null || country === 'all' ? '' : country;
  return marketFetch('/funding', tenantId, {
    params: { lens_key: lens, lens, country: countryParam },
  });
}

/** Reverse an overridable ignore reason (financial_distress / tenant_declared). */
export function overrideMarketIgnore(tenantId, accountId, payload = {}, lens) {
  return marketFetch(`/accounts/${encodeURIComponent(accountId)}/ignore/override`, tenantId, {
    method: 'POST',
    params: { lens },
    body: payload,
  });
}

/** Synchronous today; terminal status is `complete` (not `completed`). */
export function recalculateMarket(tenantId, payload = {}, lens) {
  return marketFetch('/recalculate', tenantId, { method: 'POST', params: { lens }, body: payload });
}
